import { FunctionComponent, useContext, useState } from 'react'
import styled, { ThemeContext } from 'styled-components'

import Spacer from 'components/Spacer'

import { useWindowSize } from 'services/hooks'

import * as E from 'types/enums'

import { IQuery, queryKeys } from '../types'
import BathFilter from './Filter/BathFilter'
import BedFilter from './Filter/BedFilter'
import ForSaleTypeFilter from './Filter/ForSaleTypeFilter'
import HomeTypeFilter from './Filter/HomeTypeFilter'
import MoreFilters from './Filter/MoreFilters'
import PriceFilter from './Filter/PriceFilter'
import {
  ForSaleType,
  IBathProps,
  IBedProps,
  IForSaleTypeProps,
  IHomeTypeProps,
  IPriceRangeProps,
  IRange,
  MobileFilterSheetName,
} from './types'

const FilterContainer = styled.div`
  flex-basis: 100%;
  display: flex;
  justify-content: flex-end;

  @media (min-width: ${props => props.theme.metrics.desktopMedium}px) {
    flex-basis: unset;
  }
`

interface IProps {
  onChange: (queries: Partial<IQuery>) => void
}

const Filters: FunctionComponent<IProps> = ({ onChange }) => {
  const theme = useContext(ThemeContext)
  const { width } = useWindowSize()
  const isPhoneViewport = !!(width && width < theme.metrics.tablet)

  // Current selections for filters which have not yet been applied by clicking the "Apply" button
  const [selectedForSaleTypes, setSelectedForSaleTypes] = useState<ForSaleType[]>([])
  const [selectedHomeTypes, setSelectedHomeTypes] = useState<E.HomeTypeEnum[]>([])
  const [selectedPriceRange, setSelectedPriceRange] = useState<IRange>({})
  const [selectedBedRange, setSelectedBedRange] = useState<IRange>({})
  const [selectedBathRange, setSelectedBathRange] = useState<IRange>({})

  // general purpose apply and reset functions for dropdowns and sheets that have multiple filters
  const applyFiltersForKeys = (keys: (keyof IQuery)[]) => {
    const queries: Partial<IQuery> = {}

    keys.forEach(key => {
      if (!queryKeys.includes(key)) return

      if (key === 'forSaleTypes') queries[key] = selectedForSaleTypes

      if (key === 'propertyTypes') queries[key] = selectedHomeTypes

      if (key === 'priceMin') queries[key] = selectedPriceRange.min || undefined

      if (key === 'priceMax') queries[key] = selectedPriceRange.max || undefined

      if (key === 'bedsMin') queries[key] = selectedBedRange.min || undefined

      if (key === 'bedsMax') queries[key] = selectedBedRange.max || undefined

      if (key === 'bathsMin') queries[key] = selectedBathRange.min || undefined

      if (key === 'bathsMax') queries[key] = selectedBathRange.max || undefined
    })

    onChange(queries)
  }

  const resetFiltersForKeys = (keys: (keyof IQuery)[]) => {
    const queries: Partial<IQuery> = {}

    keys.forEach(key => {
      if (!queryKeys.includes(key)) return

      if (key === 'forSaleTypes') setSelectedForSaleTypes([])

      if (key === 'propertyTypes') setSelectedHomeTypes([])

      if (key === 'priceMin' || key === 'priceMax') setSelectedPriceRange({})

      if (key === 'bedsMin' || key === 'bedsMax') setSelectedBedRange({})

      if (key === 'bathsMin' || key === 'bathsMax') setSelectedBathRange({})
    })

    keys.forEach(key => {
      if (!queryKeys.includes(key)) return

      queries[key] = undefined
    })

    onChange(queries)
  }

  // specific prop sets for components that only have one filter

  // for sale type filter
  const forSaleTypeProps: IForSaleTypeProps = {
    selectedForSaleTypes,
    setSelectedForSaleTypes,
    applyForSaleTypeSelection: () => onChange({ forSaleTypes: selectedForSaleTypes }),
    resetForSaleTypeSelection: () => {
      setSelectedForSaleTypes([])
      onChange({ forSaleTypes: [] })
    },
  }

  // price filter
  const priceRangeProps: IPriceRangeProps = {
    selectedPriceRange,
    setSelectedPriceRange,
    applyPriceRangeSelection: () =>
      onChange({
        priceMin: selectedPriceRange.min || undefined,
        priceMax: selectedPriceRange.max || undefined,
      }),
    resetPriceRangeSelection: () => {
      setSelectedPriceRange({ min: undefined, max: undefined })
      onChange({ priceMin: undefined, priceMax: undefined })
    },
  }

  // home type filter
  const homeTypeProps: IHomeTypeProps = {
    selectedHomeTypes,
    setSelectedHomeTypes,
    applyHomeTypeSelections: () => onChange({ propertyTypes: selectedHomeTypes }),
    resetHomeTypeSelections: () => {
      setSelectedHomeTypes([])
      onChange({ propertyTypes: [] })
    },
  }

  // beds filter
  const bedProps: IBedProps = {
    selectedBedRange,
    setSelectedBedRange,
    applyBedSelections: () =>
      onChange({
        bedsMin: selectedBedRange.min || undefined,
        bedsMax: selectedBedRange.max || undefined,
      }),
    resetBedSelections: () => {
      setSelectedBedRange({ min: undefined, max: undefined })
      onChange({ bedsMin: undefined, bedsMax: undefined })
    },
  }

  // baths filter
  const bathProps: IBathProps = {
    selectedBathRange,
    setSelectedBathRange,
    applyBathSelections: () =>
      onChange({
        bathsMin: selectedBathRange.min || undefined,
        bathsMax: selectedBathRange.max || undefined,
      }),
    resetBathSelections: () => {
      setSelectedBathRange({ min: undefined, max: undefined })
      onChange({ bathsMin: undefined, bathsMax: undefined })
    },
  }

  // mobile filter sheet visibility
  const [visibleMobileFilterSheet, setVisibleMobileFilterSheet]
    = useState<MobileFilterSheetName | null>(null)

  const hideMobileFilterSheet = () => setVisibleMobileFilterSheet(null)

  const getMobileFilterSheetProps = (name: MobileFilterSheetName) => ({
    visible: visibleMobileFilterSheet === name,
    showMobileFilterSheet: () => setVisibleMobileFilterSheet(name),
    hideMobileFilterSheet,
  })

  const priceFilterSheetProps = getMobileFilterSheetProps(MobileFilterSheetName.price)
  const homeTypeFilterSheetProps = getMobileFilterSheetProps(MobileFilterSheetName.homeType)
  const moreFiltersSheetProps = getMobileFilterSheetProps(MobileFilterSheetName.moreFilters)

  // must be initialized or UI will show wrong UI to start
  if (!width) return null

  // Note - we are hiding some of thse components via CSS on mobile rather than unmounting them
  // in order to preserve their internal selection state of the filters when they are hidden, which
  // may not have been applied yet
  return (
    <FilterContainer>
      <ForSaleTypeFilter
        hide={isPhoneViewport}
        {...forSaleTypeProps}
      />
      <Spacer
        size={8}
        hideMobile
      />
      <PriceFilter
        {...priceRangeProps}
        {...priceFilterSheetProps}
      />
      <Spacer size={8} />
      <HomeTypeFilter
        {...homeTypeProps}
        {...homeTypeFilterSheetProps}
      />
      <Spacer size={8} />
      <BedFilter
        hide={isPhoneViewport}
        {...bedProps}
      />
      <Spacer
        size={8}
        hideMobile
      />
      <BathFilter
        hide={isPhoneViewport}
        {...bathProps}
      />
      <MoreFilters
        hide={!isPhoneViewport}
        forSaleTypeProps={forSaleTypeProps}
        bedProps={bedProps}
        bathProps={bathProps}
        applyFiltersForKeys={applyFiltersForKeys}
        resetFiltersForKeys={resetFiltersForKeys}
        {...moreFiltersSheetProps}
      />
    </FilterContainer>
  )
}

export default Filters
